/**
 * Cài đặt → Tên miền: subdomain nền tảng (vd nhathuoc.topartners.vn) và tên miền riêng
 * (vd chat.nhathuoc.vn) — hướng dẫn bản ghi DNS, kiểm tra xác minh, trạng thái chứng chỉ.
 * Đăng nhập ở tên miền của tổ chức: trang mang thương hiệu tổ chức, chỉ người trong tổ chức vào được.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CheckCircle2, Copy, ExternalLink, Globe, Loader2, RefreshCw, Star, Trash2 } from 'lucide-react'
import { api, apiError } from '@/lib/api-client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'

interface TenMien {
  id: string; domain: string; kind: 'subdomain' | 'custom'; status: 'pending' | 'verified' | 'error'; isPrimary: boolean
  lastCheckedAt: string | null; lastError: string | null; sslStatus: 'none' | 'issued' | 'error'; sslError: string | null
  huongDan: { txt: { ten: string; giaTri: string }; troVe: { loai: string; ten: string; giaTri: string } | null; ip: string | null } | null
}
interface DuLieu {
  domains: TenMien[]; nenTang: string | null; ipMayChu: string | null
  hanMuc: { goi: { ten: string } | null; gioiHan: { tenMienRieng: number | null }; dangDung: { tenMienRieng: number } }
}

const KHOA = ['settings', 'domains']

function Sao({ v }: { v: string }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1">
      <code className="truncate rounded bg-muted px-1.5 py-0.5 text-[11px]">{v}</code>
      <button type="button" aria-label="Sao chép" className="text-muted-foreground hover:text-foreground" onClick={() => { void navigator.clipboard.writeText(v); toast.success('Đã sao chép') }}><Copy className="h-3 w-3" /></button>
    </span>
  )
}

function TrangThai({ d }: { d: TenMien }) {
  if (d.status !== 'verified') return <Badge variant="warning">Chờ xác minh DNS</Badge>
  if (d.sslStatus === 'issued') return <Badge variant="success">Đang hoạt động (HTTPS)</Badge>
  if (d.sslStatus === 'error') return <Badge variant="destructive">Lỗi cấp chứng chỉ</Badge>
  return <Badge variant="secondary">Đã xác minh · đang cấp chứng chỉ</Badge>
}

export function TenMienTab() {
  const qc = useQueryClient()
  const { data } = useQuery<DuLieu>({ queryKey: KHOA, queryFn: async () => (await api.get<DuLieu>('/settings/domains')).data })
  const [domain, setDomain] = useState('')
  const [slug, setSlug] = useState('')
  const xong = (d: DuLieu, msg?: string) => { qc.setQueryData(KHOA, d); if (msg) toast.success(msg) }
  const goi = <T, V = void>(fn: (v: V) => Promise<T>, onOk: (d: T) => void) => ({ mutationFn: fn, onSuccess: onOk, onError: (e: unknown) => toast.error(apiError(e)) })

  const them = useMutation(goi(async () => (await api.post<DuLieu>('/settings/domains', { domain })).data, (d) => { xong(d, 'Đã thêm — tạo bản ghi DNS theo hướng dẫn rồi bấm Kiểm tra'); setDomain('') }))
  const themSub = useMutation(goi(async () => (await api.post<DuLieu>('/settings/domains/subdomain', { slug })).data, (d) => { xong(d, 'Đã tạo subdomain'); setSlug('') }))
  const kiemTra = useMutation(goi(async (id: string) => (await api.post<DuLieu & { kiemTra: { ok: boolean; chiTiet: string } }>(`/settings/domains/${id}/verify`)).data,
    (d) => { xong(d); if (d.kiemTra.ok) toast.success(d.kiemTra.chiTiet); else toast.error(d.kiemTra.chiTiet) }))
  const chinh = useMutation(goi(async (id: string) => (await api.post<DuLieu>(`/settings/domains/${id}/primary`)).data, (d) => xong(d, 'Đã đặt làm tên miền chính')))
  const xoa = useMutation(goi(async (id: string) => (await api.delete<DuLieu>(`/settings/domains/${id}`)).data, (d) => xong(d, 'Đã xoá tên miền')))

  if (!data) return <div className="py-8 text-center"><Loader2 className="mx-auto animate-spin text-muted-foreground" /></div>
  const coSub = data.domains.some((d) => d.kind === 'subdomain')
  const gh = data.hanMuc.gioiHan.tenMienRieng
  const hetHan = gh != null && data.hanMuc.dangDung.tenMienRieng >= gh

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Nhân viên truy cập ứng dụng bằng tên miền của tổ chức: trang đăng nhập mang thương hiệu tổ chức (xem tab Thương hiệu) và chỉ tài khoản trong tổ chức đăng nhập được.
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        {data.nenTang && (
          <div className="space-y-2 rounded-lg border p-3">
            <p className="text-sm font-medium">Subdomain nền tảng</p>
            <p className="text-xs text-muted-foreground">Dùng được ngay, không cần cấu hình DNS.</p>
            <div className="flex items-center gap-1.5">
              <Input id="tm-slug" value={slug} disabled={coSub} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))} placeholder="nhathuocabc" className="h-9" />
              <span className="shrink-0 text-sm text-muted-foreground">.{data.nenTang}</span>
            </div>
            <Button size="sm" disabled={coSub || slug.length < 3 || themSub.isPending} onClick={() => themSub.mutate()}>{coSub ? 'Đã có subdomain' : 'Tạo subdomain'}</Button>
          </div>
        )}
        <div className="space-y-2 rounded-lg border p-3">
          <p className="text-sm font-medium">Tên miền riêng</p>
          <p className="text-xs text-muted-foreground">
            {gh == null ? 'Không giới hạn theo gói.' : gh === 0 ? 'Gói hiện tại chưa gồm tên miền riêng.' : `Gói cho phép ${gh} tên miền riêng (đang dùng ${data.hanMuc.dangDung.tenMienRieng}).`}
          </p>
          <Label htmlFor="tm-domain" className="sr-only">Tên miền</Label>
          <Input id="tm-domain" value={domain} disabled={hetHan} onChange={(e) => setDomain(e.target.value)} placeholder="chat.nhathuocabc.vn" className="h-9" />
          <Button size="sm" disabled={hetHan || domain.length < 4 || them.isPending} onClick={() => them.mutate()}>Thêm tên miền</Button>
        </div>
      </div>

      {data.domains.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground"><Globe className="mx-auto mb-2 h-6 w-6" />Chưa có tên miền nào — tổ chức đang dùng tên miền chung của nền tảng.</div>
      ) : (
        <div className="space-y-3">
          {data.domains.map((d) => (
            <div key={d.id} className="space-y-2 rounded-lg border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <a href={`https://${d.domain}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium hover:underline">{d.domain} <ExternalLink className="h-3 w-3" /></a>
                <Badge variant="secondary">{d.kind === 'subdomain' ? 'Subdomain nền tảng' : 'Tên miền riêng'}</Badge>
                <TrangThai d={d} />
                {d.isPrimary && <Badge variant="default"><Star className="mr-1 h-3 w-3" /> Chính</Badge>}
                <div className="ml-auto flex gap-1">
                  {d.status !== 'verified' && <Button size="sm" variant="outline" disabled={kiemTra.isPending} onClick={() => kiemTra.mutate(d.id)}><RefreshCw className={kiemTra.isPending ? 'animate-spin' : ''} /> Kiểm tra</Button>}
                  {d.status === 'verified' && !d.isPrimary && <Button size="sm" variant="ghost" onClick={() => chinh.mutate(d.id)}>Đặt làm chính</Button>}
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => { if (confirm(`Xoá ${d.domain}? Người dùng sẽ không vào được qua tên miền này nữa.`)) xoa.mutate(d.id) }}><Trash2 /></Button>
                </div>
              </div>
              {d.huongDan && d.status !== 'verified' && (
                <div className="space-y-1.5 rounded-md bg-muted/40 p-2.5 text-xs">
                  <p className="font-medium">Tạo 2 bản ghi DNS tại nhà cung cấp tên miền:</p>
                  <div className="grid gap-1 sm:grid-cols-[70px_1fr_1fr]">
                    <span className="text-muted-foreground">TXT</span><Sao v={d.huongDan.txt.ten} /><Sao v={d.huongDan.txt.giaTri} />
                    {d.huongDan.troVe && <><span className="text-muted-foreground">{d.huongDan.troVe.loai}</span><Sao v={d.huongDan.troVe.ten} /><Sao v={d.huongDan.troVe.giaTri} /></>}
                  </div>
                  <p className="text-muted-foreground">DNS có thể mất 5–30 phút để cập nhật. Hệ thống tự kiểm tra lại mỗi 5 phút.</p>
                </div>
              )}
              {(d.lastError || d.sslError) && <p className="text-xs text-warning">{d.sslError ?? d.lastError}</p>}
              {d.status === 'verified' && d.sslStatus === 'issued' && <p className="flex items-center gap-1 text-xs text-success"><CheckCircle2 className="h-3.5 w-3.5" /> Truy cập https://{d.domain} để đăng nhập.</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
